import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * MAGE TOWER (Башня мага) — a wizard's high sanctum: tall, narrow, humming with quiet arcane wonder.
 *
 * Composition (one clear hero + a tidy nook + open floor):
 *  • HERO, dead-centre: a great glowing CRYSTAL ORB floating over a SPELL CIRCLE etched into the floor,
 *    with a scrying chandelier of floating candles hung high above — the single brightest, most magnetic
 *    beat. Everything else defers to it.
 *  • The scholar's NOOK (W of centre): a sit-able chair on the circle, its desk + open spell-book lectern,
 *    pooled by ONE warm candelabra so the cold violet magic never reads grim. SEAT-MATCH at the chair.
 *  • On-narrative APPARATUS hugging the E wall: a bubbling alembic above, a scroll-shelf of tomes below.
 *  • Wall dressing kept spare: the mage's sigil banner + a single torch on the W wall.
 *
 * Deliberately UNDER-dressed: the centre and SE floor are left open to breathe. We dropped the second
 * wall-torch and the corner relic-pedestal — the room had four competing glow-pools and a heaped SW
 * corner; now it reads as a designed sanctum, not a curiosity heap.
 *
 * Geometry: zone (40,2040) 320×600 → centre (200,2340). N door (x200) and E door (y2340) kept clear.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor FIRST: the great arcane SPELL CIRCLE, etched dead-centre, laid under everything. ──
  place(ctx, g.spellCircle(art, 80), 200, 2340);

  // ── HERO: the glowing CRYSTAL ORB on its plinth + the floating-candle chandelier hung high above the
  //    circle — the unmistakable violet focal point and the room's brightest beat. ──
  place(ctx, kit.chandelier(art), 200, 2180);
  place(ctx, g.crystalOrb(art), 200, 2240);

  // ── The scholar's NOOK: a sit-able chair ON the spell circle, its desk + open spell-book in the glow.
  //    SEAT-MATCH — woodChair sits at the exact mage-tower-chair anchor (200,2340). ──
  place(ctx, kit.smallTable(art, 34), 140, 2400); // the desk
  place(ctx, kit.lectern(art), 140, 2400); // the open spell-book on the desk
  place(ctx, kit.woodChair(art, Math.PI / 2), 200, 2340); // faces the desk
  place(ctx, kit.candelabra(art), 110, 2440); // ONE warm pool of light on the nook

  // ── On-narrative APPARATUS, hugging the E wall (off the E doorway at y2340). ──
  place(ctx, g.alembic(art), 290, 2200); // bubbling alembic, NE
  place(ctx, kit.scrollShelf(art, 70, 90), 290, 2480); // tomes & scrolls, SE far end

  // ── Spare wall dressing: the mage's sigil banner + a single torch on the W wall, clear of both doors. ──
  place(ctx, kit.banner(art, 0x5b3a8a, 110), 70, 2200); // arcane-violet sigil banner, W wall
  place(ctx, kit.wallTorch(art), 70, 2470); // SW wall torch
};

export default decorate;
